/**
 * useShoppingWebSocket Hook
 *
 * Manages WebSocket connection for real-time shopping notifications.
 * Connects to the server's shopping WebSocket endpoint for:
 * - Real-time purchase interception alerts
 * - Decision sync across devices
 * - Push notifications for purchases
 *
 * Features:
 * - Auto-reconnection with exponential backoff
 * - Authentication handling
 * - Connection state management
 * - Event-based message handling
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// WebSocket message types (matching server)
export interface WebSocketMessage {
  type: 'purchase_update' | 'auth_status' | 'sync_request' | 'notification';
  data: any;
  timestamp: number;
  requestId?: string;
}

export interface PurchaseNotification {
  category: 'purchase';
  type: 'captured' | 'approved' | 'rejected' | 'queued';
  purchaseId: string;
  productTitle: string;
  price: number;
  decision?: {
    decision: 'green' | 'yellow' | 'red';
    confidence: number;
    reasoning: string;
  };
}

export interface ConnectionState {
  isConnected: boolean;
  isAuthenticated: boolean;
  isMobile: boolean;
  lastError: string | null;
  reconnectAttempts: number;
}

export interface UseShoppingWebSocketOptions {
  /** WebSocket endpoint URL (default: auto-detect) */
  url?: string;
  /** Auto-connect on mount (default: true) */
  autoConnect?: boolean;
  /** Max reconnection attempts (default: 5) */
  maxReconnectAttempts?: number;
  /** Callback when purchase notification received */
  onPurchaseNotification?: (notification: PurchaseNotification) => void;
  /** Callback when purchase update received */
  onPurchaseUpdate?: (data: any) => void;
  /** Callback when connection state changes */
  onConnectionChange?: (state: ConnectionState) => void;
}

export interface UseShoppingWebSocketReturn {
  /** Current connection state */
  connectionState: ConnectionState;
  /** Connect to WebSocket server */
  connect: () => void;
  /** Disconnect from WebSocket server */
  disconnect: () => void;
  /** Send a message to the server */
  sendMessage: (message: WebSocketMessage) => boolean;
  /** Request data sync */
  requestSync: () => void;
  /** Send purchase action (approve/reject/delay) */
  sendPurchaseAction: (purchaseId: string, action: 'approve' | 'reject' | 'delay') => void;
}

// Default WebSocket URL detection
function getDefaultWebSocketUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;

  // In development, use the API server's WebSocket endpoint
  if (import.meta.env.DEV) {
    return 'ws://localhost:3000/ws/shopping';
  }

  return `${protocol}//${host}/ws/shopping`;
}

export function useShoppingWebSocket(
  options: UseShoppingWebSocketOptions = {}
): UseShoppingWebSocketReturn {
  const {
    url = getDefaultWebSocketUrl(),
    autoConnect = true,
    maxReconnectAttempts = 5,
    onPurchaseNotification,
    onPurchaseUpdate,
    onConnectionChange,
  } = options;

  // Connection state
  const [connectionState, setConnectionState] = useState<ConnectionState>({
    isConnected: false,
    isAuthenticated: false,
    isMobile: false,
    lastError: null,
    reconnectAttempts: 0,
  });

  // Refs for WebSocket instance and reconnection
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isUnmountedRef = useRef(false);

  // Update state and notify listener
  const updateConnectionState = useCallback(
    (updates: Partial<ConnectionState>) => {
      setConnectionState((prev) => {
        const newState = { ...prev, ...updates };
        onConnectionChange?.(newState);
        return newState;
      });
    },
    [onConnectionChange]
  );

  // Get auth token
  const getAuthToken = useCallback((): string | null => {
    return localStorage.getItem('authToken');
  }, []);

  // Send message to server
  const sendMessage = useCallback((message: WebSocketMessage): boolean => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('[WebSocket] Cannot send message - not connected');
      return false;
    }

    try {
      wsRef.current.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error('[WebSocket] Error sending message:', error);
      return false;
    }
  }, []);

  // Authenticate with server
  const authenticate = useCallback(() => {
    const token = getAuthToken();
    if (!token) {
      console.warn('[WebSocket] No auth token available');
      updateConnectionState({ lastError: 'No authentication token' });
      return;
    }

    sendMessage({
      type: 'auth_status',
      data: { token },
      timestamp: Date.now(),
    });
  }, [getAuthToken, sendMessage, updateConnectionState]);

  // Handle incoming messages
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);

        switch (message.type) {
          case 'auth_status':
            if (message.data.authenticated) {
              updateConnectionState({
                isAuthenticated: true,
                isMobile: message.data.isMobile || false,
                lastError: null,
              });
              console.log('[WebSocket] Authenticated successfully');
            } else if (message.data.connected) {
              // Initial connection, need to authenticate
              authenticate();
            }
            break;

          case 'notification':
            if (message.data.category === 'purchase') {
              onPurchaseNotification?.(message.data as PurchaseNotification);
            } else if (message.data.category === 'error') {
              console.error('[WebSocket] Server error:', message.data.message);
              updateConnectionState({ lastError: message.data.message });
            }
            break;

          case 'purchase_update':
            onPurchaseUpdate?.(message.data);
            break;

          case 'sync_request':
            // Server requesting sync - emit event for handlers
            console.log('[WebSocket] Sync request received');
            break;

          default:
            console.log('[WebSocket] Unknown message type:', message.type);
        }
      } catch (error) {
        console.error('[WebSocket] Error parsing message:', error);
      }
    },
    [authenticate, onPurchaseNotification, onPurchaseUpdate, updateConnectionState]
  );

  // Connect to WebSocket server
  const connect = useCallback(() => {
    // Don't connect if already connected or unmounted
    if (wsRef.current?.readyState === WebSocket.OPEN || isUnmountedRef.current) {
      return;
    }

    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    try {
      console.log('[WebSocket] Connecting to:', url);
      const ws = new WebSocket(url);

      ws.onopen = () => {
        console.log('[WebSocket] Connected');
        updateConnectionState({
          isConnected: true,
          lastError: null,
          reconnectAttempts: 0,
        });
      };

      ws.onmessage = handleMessage;

      ws.onerror = (event) => {
        console.error('[WebSocket] Connection error:', event);
        updateConnectionState({ lastError: 'Connection error' });
      };

      ws.onclose = (event) => {
        console.log('[WebSocket] Connection closed:', event.code, event.reason);
        wsRef.current = null;

        updateConnectionState({
          isConnected: false,
          isAuthenticated: false,
        });

        // Attempt reconnection if not intentionally closed
        if (!isUnmountedRef.current && event.code !== 1000) {
          setConnectionState((prev) => {
            if (prev.reconnectAttempts < maxReconnectAttempts) {
              const delay = Math.min(1000 * Math.pow(2, prev.reconnectAttempts), 30000);
              console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${prev.reconnectAttempts + 1})`);

              reconnectTimeoutRef.current = setTimeout(() => {
                connect();
              }, delay);

              return { ...prev, reconnectAttempts: prev.reconnectAttempts + 1 };
            } else {
              console.log('[WebSocket] Max reconnection attempts reached');
              return { ...prev, lastError: 'Max reconnection attempts reached' };
            }
          });
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('[WebSocket] Error creating connection:', error);
      updateConnectionState({ lastError: 'Failed to create connection' });
    }
  }, [url, maxReconnectAttempts, handleMessage, updateConnectionState]);

  // Disconnect from WebSocket server
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }

    updateConnectionState({
      isConnected: false,
      isAuthenticated: false,
      reconnectAttempts: 0,
    });
  }, [updateConnectionState]);

  // Request data sync
  const requestSync = useCallback(() => {
    sendMessage({
      type: 'sync_request',
      data: {},
      timestamp: Date.now(),
      requestId: `sync-${Date.now()}`,
    });
  }, [sendMessage]);

  // Send purchase action
  const sendPurchaseAction = useCallback(
    (purchaseId: string, action: 'approve' | 'reject' | 'delay') => {
      sendMessage({
        type: 'purchase_update',
        data: {
          purchaseId,
          action,
          timestamp: Date.now(),
        },
        timestamp: Date.now(),
        requestId: `action-${purchaseId}-${Date.now()}`,
      });
    },
    [sendMessage]
  );

  // Auto-connect on mount
  useEffect(() => {
    isUnmountedRef.current = false;

    if (autoConnect) {
      // Small delay to ensure auth token is available
      const timeout = setTimeout(() => {
        if (getAuthToken()) {
          connect();
        }
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [autoConnect, connect, getAuthToken]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
      disconnect();
    };
  }, [disconnect]);

  return {
    connectionState,
    connect,
    disconnect,
    sendMessage,
    requestSync,
    sendPurchaseAction,
  };
}

export default useShoppingWebSocket;
